import type { FC } from 'react';
import React, { memo, useState, useCallback } from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import GlobeIcon from '@atlaskit/icon/core/globe';
import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import type { NewCoreIconProps } from '@atlaskit/icon/base-new';

const spaceImgStyles = cssMap({
	default: {
		flexShrink: 0,
		border: 0,
		backgroundColor: token('color.skeleton'),
	},
	loaded: {
		backgroundColor: 'transparent',
	},
});

const AvatarStyles = cssMap({
	xsmall: { borderRadius: token('radius.small'), width: '20px', height: '20px' },
	small: { borderRadius: token('radius.xsmall'), width: '24px', height: '24px' },
	medium: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		width: '32px',
		height: '32px',
	},
	large: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		width: '40px',
		height: '40px',
	},
	xlarge: { borderRadius: token('radius.medium'), width: '96px', height: '96px' },
});

export const types: {
	global: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
	personal: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
} = {
	global: GlobeIcon,
	personal: PersonAvatarIcon,
};

type Size = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

export interface SpaceIconProps {
	type?: string;
	label?: string;
	size?: Size;
	src?: string;
	testId?: string;
}

export const SpaceIcon: FC<SpaceIconProps> = memo(
	({ type, label = '', size = 'medium', src, testId }) => {
		const [imgLoaded, setImgLoaded] = useState(!src);
		const setLoaded = useCallback(() => {
			setImgLoaded(true);
		}, [setImgLoaded]);

		if (src) {
			return (
				<img
					css={[
						spaceImgStyles['default'],
						imgLoaded && spaceImgStyles['loaded'],
						AvatarStyles[size],
					]}
					src={src?.includes('/wiki') || src?.startsWith('http') ? src : `/wiki${src}`}
					alt={label}
					onLoad={setLoaded}
					onError={setLoaded}
					data-testid={testId}
				/>
			);
		}

		const Icon: React.ElementType = (type && types[type as keyof typeof types]) || GlobeIcon;

		return <Icon label={label} testId={testId} />;
	},
);
